// src/_store/jobpostingsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const baseUrl = `${process.env.REACT_APP_API_URL}`;

// ============================================
// ASYNC THUNKS - CAPTCHA
// ============================================

// Generate CAPTCHA
// export const generateCaptcha = createAsyncThunk(
//   'jobPostings/generateCaptcha',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await fetch(`${baseUrl}/captcha/generate`, {
//         method: 'GET',
//         headers: { 'Content-Type': 'application/json' },
//       });
//       const data = await response.json();
//       if (!response.ok) return rejectWithValue(data);
//       return data;
//     } catch (error) {
//       return rejectWithValue({ message: error.message });
//     }
//   }
// );

// // Verify CAPTCHA
// export const verifyCaptcha = createAsyncThunk(
//   'jobPostings/verifyCaptcha',
//   async ({ captchaId, captchaInput }, { rejectWithValue }) => {
//     try {
//       const response = await fetch(`${baseUrl}/captcha/verify`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ captchaId, captchaInput }),
//       });
//       const data = await response.json();
//       if (!response.ok) return rejectWithValue(data);
//       return data;
//     } catch (error) {
//       return rejectWithValue({ message: error.message });
//     }
//   }
// );

// ============================================
// ASYNC THUNKS - JOB VERIFICATION FLOW
// ============================================


export const requestJobVerification = createAsyncThunk(
  'jobPostings/requestJobVerification',
  async (jobData, { rejectWithValue }) => {
    try {
      // Create a clean payload without the captchaId
      const cleanData = {
        name: jobData.name,
        email: jobData.email,
        contactNumber: jobData.contactNumber,
        location: jobData.location,
        description: jobData.description
      };

      const response = await fetch(`${baseUrl}/jobs/verify-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData), // Send only the clean data
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Step 2: Verify code and post all pending jobs
export const verifyAndPostJob = createAsyncThunk(
  'jobPostings/verifyAndPostJob',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/jobs/verify-and-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// ============================================
// ASYNC THUNKS - PUBLIC JOB LISTINGS
// ============================================

// Fetch approved job openings (public view)
export const fetchJobOpenings = createAsyncThunk(
  'jobPostings/fetchJobOpenings',
  async ({ status = 'approved', limit = 50, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${baseUrl}/job-openings?status=${status}&limit=${limit}&offset=${offset}`
      );
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Fetch single job opening details (for admin review)
export const fetchJobOpeningById = createAsyncThunk(
  'jobPostings/fetchJobOpeningById',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/job-openings/${jobId}`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// ============================================
// NEW ASYNC THUNKS - JOB APPLICATIONS
// ============================================

// Fetch single job by ID (for authenticated Jobs page)
export const fetchSingleJob = createAsyncThunk(
  'jobPostings/fetchSingleJob',
  async ({ jobId, userId = null }, { rejectWithValue }) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (userId) {
        headers['X-User-Id'] = userId;
      }
      
      const response = await fetch(`${baseUrl}/jobs/${jobId}`, {
        method: 'GET',
        headers: headers,
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Submit job application
export const submitJobApplication = createAsyncThunk(
  'jobPostings/submitJobApplication',
  async ({ jobId, userId }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/jobs/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
        },
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Fetch user's applied jobs
export const fetchUserApplications = createAsyncThunk(
  'jobPostings/fetchUserApplications',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/user/applications`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
        },
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// ============================================
// ASYNC THUNKS - ADMIN ENDPOINTS
// ============================================

// Admin: Fetch pending jobs (awaiting approval)
export const fetchPendingJobs = createAsyncThunk(
  'jobPostings/fetchPendingJobs',
  async (adminPassword, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/admin/pending-jobs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Admin: Approve job posting
export const approveJobPosting = createAsyncThunk(
  'jobPostings/approveJobPosting',
  async ({ jobId, adminPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/admin/approve-job/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({ isApproved: true, status: 'approved' }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { jobId, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Admin: Reject job posting
export const rejectJobPosting = createAsyncThunk(
  'jobPostings/rejectJobPosting',
  async ({ jobId, rejectionReason, adminPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/admin/reject-job/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({ 
          isApproved: false, 
          status: 'rejected',
          rejectionReason 
        }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { jobId, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Admin: Update enhanced description before approval
export const updateEnhancedDescription = createAsyncThunk(
  'jobPostings/updateEnhancedDescription',
  async ({ jobId, enhancedDescription, adminPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/admin/update-description/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({ enhancedDescription }),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return { jobId, enhancedDescription, ...data };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// Admin: Fetch job posting statistics
export const fetchJobStats = createAsyncThunk(
  'jobPostings/fetchJobStats',
  async (adminPassword, { rejectWithValue }) => {
    try {
      const response = await fetch(`${baseUrl}/admin/job-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Password': adminPassword,
        },
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  // Data
  jobOpenings: [],
  pendingJobs: [],
  currentJobOpening: null,
  jobStats: null,
  verificationData: null,
  createdJobs: [],
  
  // NEW: Application related data
  singleJob: null,
  userApplications: [],
  applicationSubmission: null,
  
  // CAPTCHA data
  // captchaId: null,
  // captchaImage: null,
  // captchaVerified: false,
  
  // Status flags
  loading: false,
  error: null,
  success: false,
  
  // Individual operation statuses
  createStatus: 'idle',
  postStatus: 'idle',
  fetchOpeningsStatus: 'idle',
  fetchPendingStatus: 'idle',
  fetchJobByIdStatus: 'idle',
  approveStatus: 'idle',
  rejectStatus: 'idle',
  updateDescriptionStatus: 'idle',
  fetchStatsStatus: 'idle',
  // generateCaptchaStatus: 'idle',
  // verifyCaptchaStatus: 'idle',
  
  // NEW: Application statuses
  fetchSingleJobStatus: 'idle',
  submitApplicationStatus: 'idle',
  fetchApplicationsStatus: 'idle',
  
  // Additional metadata
  verificationExpiry: null,
  totalPendingJobs: 0,
  isUserVerified: false,
};

// ============================================
// SLICE DEFINITION
// ============================================

const jobPostingsSlice = createSlice({
  name: 'jobPostings',
  initialState,
  reducers: {
    clearJobPostingsState: (state) => {
      state.jobOpenings = [];
      state.pendingJobs = [];
      state.currentJobOpening = null;
      state.jobStats = null;
      state.verificationData = null;
      state.createdJobs = [];
      state.singleJob = null;
      state.userApplications = [];
      state.applicationSubmission = null;
      // state.captchaId = null;
      // state.captchaImage = null;
      // state.captchaVerified = false;
      state.error = null;
      state.success = false;
      state.verificationExpiry = null;
      state.totalPendingJobs = 0;
      state.isUserVerified = false;
      
      state.createStatus = 'idle';
      state.postStatus = 'idle';
      state.fetchOpeningsStatus = 'idle';
      state.fetchPendingStatus = 'idle';
      state.fetchJobByIdStatus = 'idle';
      state.approveStatus = 'idle';
      state.rejectStatus = 'idle';
      state.updateDescriptionStatus = 'idle';
      state.fetchStatsStatus = 'idle';
      // state.generateCaptchaStatus = 'idle';
      // state.verifyCaptchaStatus = 'idle';
      state.fetchSingleJobStatus = 'idle';
      state.submitApplicationStatus = 'idle';
      state.fetchApplicationsStatus = 'idle';
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    clearSuccess: (state) => {
      state.success = false;
    },
    
    setVerificationData: (state, action) => {
      state.verificationData = action.payload;
      state.verificationExpiry = action.payload?.data?.verification_expiry;
      state.totalPendingJobs = action.payload?.data?.total_pending_jobs || 
                               action.payload?.data?.pending_jobs_count || 0;
      state.isUserVerified = action.payload?.data?.user_verified || false;
    },
    
    clearCurrentJob: (state) => {
      state.currentJobOpening = null;
    },
    
    // clearCaptchaVerified: (state) => {
    //   state.captchaVerified = false;
    // },
    
    // NEW: Clear single job
    clearSingleJob: (state) => {
      state.singleJob = null;
    },
    
    // NEW: Clear application submission
    clearApplicationSubmission: (state) => {
      state.applicationSubmission = null;
    },
  },
  
  extraReducers: (builder) => {
    builder
      // ============================================
      // GENERATE CAPTCHA
      // ============================================
      // .addCase(generateCaptcha.pending, (state) => {
      //   state.generateCaptchaStatus = 'loading';
      //   state.error = null;
      // })
      // .addCase(generateCaptcha.fulfilled, (state, action) => {
      //   state.generateCaptchaStatus = 'succeeded';
      //   state.captchaId = action.payload?.captchaId || null;
      //   state.captchaImage = action.payload?.captchaImage || null;
      //   state.captchaVerified = false;
      // })
      // .addCase(generateCaptcha.rejected, (state, action) => {
      //   state.generateCaptchaStatus = 'failed';
      //   state.error = action.payload?.message || 'Failed to generate CAPTCHA';
      // })
      
      // // ============================================
      // // VERIFY CAPTCHA
      // // ============================================
      // .addCase(verifyCaptcha.pending, (state) => {
      //   state.verifyCaptchaStatus = 'loading';
      //   state.error = null;
      // })
      // .addCase(verifyCaptcha.fulfilled, (state, action) => {
      //   state.verifyCaptchaStatus = 'succeeded';
      //   state.captchaVerified = action.payload?.valid || false;
      // })
      // .addCase(verifyCaptcha.rejected, (state, action) => {
      //   state.verifyCaptchaStatus = 'failed';
      //   state.error = action.payload?.message || 'CAPTCHA verification failed';
      //   state.captchaVerified = false;
      // })
      
      // REQUEST JOB VERIFICATION
      .addCase(requestJobVerification.pending, (state) => {
        state.createStatus = 'loading';
        state.error = null;
        state.success = false;
      })
      .addCase(requestJobVerification.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        state.verificationData = action.payload;
        
        const payload = action.payload;
        state.verificationExpiry = payload?.data?.verification_expiry || null;
        state.totalPendingJobs = payload?.data?.total_pending_jobs || 
                                 payload?.data?.pending_jobs_count || 0;
        state.isUserVerified = payload?.data?.user_verified || false;
        
        if (payload?.data?.user_verified) {
          state.success = true;
        }
        
        // Reset CAPTCHA after successful submission
        // state.captchaVerified = false;
        // state.captchaId = null;
        // state.captchaImage = null;
      })
      .addCase(requestJobVerification.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.error = action.payload?.message || 'Failed to send verification code';
        state.success = false;
      })
      
      // VERIFY AND POST JOB
      .addCase(verifyAndPostJob.pending, (state) => {
        state.postStatus = 'loading';
        state.error = null;
        state.success = false;
      })
      .addCase(verifyAndPostJob.fulfilled, (state, action) => {
        state.postStatus = 'succeeded';
        state.success = true;
        state.createdJobs = action.payload?.jobs || [];
        state.totalPendingJobs = 0;
        state.isUserVerified = true;
        state.verificationExpiry = null;
      })
      .addCase(verifyAndPostJob.rejected, (state, action) => {
        state.postStatus = 'failed';
        state.error = action.payload?.message || 'Verification failed';
        state.success = false;
      })
      
      // FETCH JOB OPENINGS
      .addCase(fetchJobOpenings.pending, (state) => {
        state.fetchOpeningsStatus = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobOpenings.fulfilled, (state, action) => {
        state.fetchOpeningsStatus = 'succeeded';
        state.loading = false;
        state.jobOpenings = action.payload?.data || action.payload || [];
      })
      .addCase(fetchJobOpenings.rejected, (state, action) => {
        state.fetchOpeningsStatus = 'failed';
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch job openings';
      })
      
      // FETCH JOB OPENING BY ID (for admin review)
      .addCase(fetchJobOpeningById.pending, (state) => {
        state.fetchJobByIdStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchJobOpeningById.fulfilled, (state, action) => {
        state.fetchJobByIdStatus = 'succeeded';
        state.currentJobOpening = action.payload?.data || action.payload || null;
      })
      .addCase(fetchJobOpeningById.rejected, (state, action) => {
        state.fetchJobByIdStatus = 'failed';
        state.error = action.payload?.message || 'Failed to fetch job details';
      })
      
      // ============================================
      // NEW: FETCH SINGLE JOB
      // ============================================
      .addCase(fetchSingleJob.pending, (state) => {
        state.fetchSingleJobStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchSingleJob.fulfilled, (state, action) => {
        state.fetchSingleJobStatus = 'succeeded';
        state.singleJob = action.payload?.data || action.payload || null;
      })
      .addCase(fetchSingleJob.rejected, (state, action) => {
        state.fetchSingleJobStatus = 'failed';
        state.error = action.payload?.message || 'Failed to fetch job';
      })
      
      // ============================================
      // NEW: SUBMIT JOB APPLICATION
      // ============================================
      .addCase(submitJobApplication.pending, (state) => {
        state.submitApplicationStatus = 'loading';
        state.error = null;
        state.success = false;
      })
      .addCase(submitJobApplication.fulfilled, (state, action) => {
        state.submitApplicationStatus = 'succeeded';
        state.success = true;
        state.applicationSubmission = action.payload?.data || action.payload || null;
      })
      .addCase(submitJobApplication.rejected, (state, action) => {
        state.submitApplicationStatus = 'failed';
        state.error = action.payload?.message || 'Failed to submit application';
        state.success = false;
      })
      
      // ============================================
      // NEW: FETCH USER APPLICATIONS
      // ============================================
      .addCase(fetchUserApplications.pending, (state) => {
        state.fetchApplicationsStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchUserApplications.fulfilled, (state, action) => {
        state.fetchApplicationsStatus = 'succeeded';
        state.userApplications = action.payload?.data?.applications || 
                                 action.payload?.applications || 
                                 action.payload?.data || 
                                 action.payload || [];
      })
      .addCase(fetchUserApplications.rejected, (state, action) => {
        state.fetchApplicationsStatus = 'failed';
        state.error = action.payload?.message || 'Failed to fetch applications';
      })
      
      // FETCH PENDING JOBS (ADMIN)
      .addCase(fetchPendingJobs.pending, (state) => {
        state.fetchPendingStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchPendingJobs.fulfilled, (state, action) => {
        state.fetchPendingStatus = 'succeeded';
        state.pendingJobs = action.payload?.data || action.payload || [];
      })
      .addCase(fetchPendingJobs.rejected, (state, action) => {
        state.fetchPendingStatus = 'failed';
        state.error = action.payload?.message || 'Failed to fetch pending jobs';
      })
      
      // APPROVE JOB POSTING (ADMIN)
      .addCase(approveJobPosting.pending, (state) => {
        state.approveStatus = 'loading';
        state.error = null;
      })
      .addCase(approveJobPosting.fulfilled, (state, action) => {
        state.approveStatus = 'succeeded';
        state.success = true;
        state.pendingJobs = state.pendingJobs.filter(
          (job) => job.id !== action.payload.jobId && job._id !== action.payload.jobId
        );
      })
      .addCase(approveJobPosting.rejected, (state, action) => {
        state.approveStatus = 'failed';
        state.error = action.payload?.message || 'Failed to approve job';
      })
      
      // REJECT JOB POSTING (ADMIN)
      .addCase(rejectJobPosting.pending, (state) => {
        state.rejectStatus = 'loading';
        state.error = null;
      })
      .addCase(rejectJobPosting.fulfilled, (state, action) => {
        state.rejectStatus = 'succeeded';
        state.success = true;
        state.pendingJobs = state.pendingJobs.filter(
          (job) => job.id !== action.payload.jobId && job._id !== action.payload.jobId
        );
      })
      .addCase(rejectJobPosting.rejected, (state, action) => {
        state.rejectStatus = 'failed';
        state.error = action.payload?.message || 'Failed to reject job';
      })
      
      // UPDATE ENHANCED DESCRIPTION (ADMIN)
      .addCase(updateEnhancedDescription.pending, (state) => {
        state.updateDescriptionStatus = 'loading';
        state.error = null;
      })
      .addCase(updateEnhancedDescription.fulfilled, (state, action) => {
        state.updateDescriptionStatus = 'succeeded';
        state.success = true;
        
        const jobIndex = state.pendingJobs.findIndex(
          (job) => job.id === action.payload.jobId || job._id === action.payload.jobId
        );
        if (jobIndex !== -1) {
          state.pendingJobs[jobIndex].enhancedDescription = action.payload.enhancedDescription;
        }
        if (state.currentJobOpening?.id === action.payload.jobId || 
            state.currentJobOpening?._id === action.payload.jobId) {
          state.currentJobOpening.enhancedDescription = action.payload.enhancedDescription;
        }
      })
      .addCase(updateEnhancedDescription.rejected, (state, action) => {
        state.updateDescriptionStatus = 'failed';
        state.error = action.payload?.message || 'Failed to update description';
      })
      
      // FETCH JOB STATS (ADMIN)
      .addCase(fetchJobStats.pending, (state) => {
        state.fetchStatsStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchJobStats.fulfilled, (state, action) => {
        state.fetchStatsStatus = 'succeeded';
        state.jobStats = action.payload?.data || action.payload || null;
      })
      .addCase(fetchJobStats.rejected, (state, action) => {
        state.fetchStatsStatus = 'failed';
        state.error = action.payload?.message || 'Failed to fetch job statistics';
      });
  },
});

// ============================================
// ACTIONS
// ============================================

export const { 
  clearJobPostingsState, 
  clearError, 
  clearSuccess,
  setVerificationData,
  clearCurrentJob,
  // clearCaptchaVerified,
  clearSingleJob,
  clearApplicationSubmission,
} = jobPostingsSlice.actions;

// ============================================
// SELECTORS
// ============================================

export const selectJobOpenings = (state) => state.jobPostings?.jobOpenings || [];
export const selectPendingJobs = (state) => state.jobPostings?.pendingJobs || [];
export const selectCurrentJobOpening = (state) => state.jobPostings?.currentJobOpening || null;
export const selectJobStats = (state) => state.jobPostings?.jobStats || null;
export const selectVerificationData = (state) => state.jobPostings?.verificationData || null;
export const selectCreatedJobs = (state) => state.jobPostings?.createdJobs || [];

// NEW: Application selectors
export const selectSingleJob = (state) => state.jobPostings?.singleJob || null;
export const selectUserApplications = (state) => state.jobPostings?.userApplications || [];
export const selectApplicationSubmission = (state) => state.jobPostings?.applicationSubmission || null;

// CAPTCHA selectors
// export const selectCaptchaId = (state) => state.jobPostings?.captchaId || null;
// export const selectCaptchaImage = (state) => state.jobPostings?.captchaImage || null;
// export const selectCaptchaVerified = (state) => state.jobPostings?.captchaVerified || false;

export const selectLoading = (state) => state.jobPostings?.loading || false;
export const selectError = (state) => state.jobPostings?.error || null;
export const selectSuccess = (state) => state.jobPostings?.success || false;

export const selectCreateStatus = (state) => state.jobPostings?.createStatus || 'idle';
export const selectPostStatus = (state) => state.jobPostings?.postStatus || 'idle';
export const selectFetchOpeningsStatus = (state) => state.jobPostings?.fetchOpeningsStatus || 'idle';
export const selectFetchPendingStatus = (state) => state.jobPostings?.fetchPendingStatus || 'idle';
export const selectFetchJobByIdStatus = (state) => state.jobPostings?.fetchJobByIdStatus || 'idle';
export const selectApproveStatus = (state) => state.jobPostings?.approveStatus || 'idle';
export const selectRejectStatus = (state) => state.jobPostings?.rejectStatus || 'idle';
export const selectUpdateDescriptionStatus = (state) => state.jobPostings?.updateDescriptionStatus || 'idle';
export const selectFetchStatsStatus = (state) => state.jobPostings?.fetchStatsStatus || 'idle';
// export const selectGenerateCaptchaStatus = (state) => state.jobPostings?.generateCaptchaStatus || 'idle';
// export const selectVerifyCaptchaStatus = (state) => state.jobPostings?.verifyCaptchaStatus || 'idle';

// NEW: Application status selectors
export const selectFetchSingleJobStatus = (state) => state.jobPostings?.fetchSingleJobStatus || 'idle';
export const selectSubmitApplicationStatus = (state) => state.jobPostings?.submitApplicationStatus || 'idle';
export const selectFetchApplicationsStatus = (state) => state.jobPostings?.fetchApplicationsStatus || 'idle';

export const selectVerificationExpiry = (state) => state.jobPostings?.verificationExpiry || null;
export const selectTotalPendingJobs = (state) => state.jobPostings?.totalPendingJobs || 0;
export const selectIsUserVerified = (state) => state.jobPostings?.isUserVerified || false;

// ============================================
// DEFAULT EXPORT
// ============================================

export default jobPostingsSlice.reducer;